import { z } from 'zod';
import { IsoTime } from './basics.js';
import { CheckException } from './compliance-posture.js';
import { MITRE_TACTIC_IDS, MitreTechniqueId } from './mitre.js';
/*
 * What a server's Wazuh agent already finds, read by the central API from the
 * manager's API (build plan P11.8.6): configuration assessment (SCA) — each
 * policy's checks, passed, failed or not applicable, with why and how to fix —
 * and rootcheck's findings. Shapes checked against what 4.14.8 answers
 * (`packages/api/test/fixtures/wazuh-api/4.14.8/`). Everything here is the
 * manager's text, from policy files Wazuh ships; bounded all the same.
 */
export const SCA_LIMITS = {
    title: 300,
    rationale: 4000,
    remediation: 8000,
    reason: 500,
    /** What the check looked at: its commands, files or directories. */
    subject: 1000,
    complianceKeys: 30,
    complianceValues: 40,
    complianceValue: 64,
};
export const ScaCheckResult = z.enum(['passed', 'failed', 'not applicable']);
/** `cis_ubuntu24-04`: a policy file's name, which Wazuh uses as its id. */
export const ScaPolicyId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9._-]+$/);
/** Standard → its references (`cis: ['1.1.2.1.1']`, `pci_dss_v4.0: ['1.2.5', '2.2.4']`). */
export const ScaCompliance = z
    .record(z
    .string()
    .min(1)
    .max(64)
    .regex(/^[\w.-]+$/), z.array(z.string().min(1).max(SCA_LIMITS.complianceValue)).max(SCA_LIMITS.complianceValues))
    .refine((value) => Object.keys(value).length <= SCA_LIMITS.complianceKeys, {
    message: 'too many standards',
});
/** One policy on one server, as its last scan left it. */
export const ScaPolicySummary = z.object({
    policyId: ScaPolicyId,
    name: z.string().min(1).max(300),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    notApplicable: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    /** Wazuh's: passed of passed and failed, per cent. */
    score: z.number().int().min(0).max(100),
    scannedAt: IsoTime,
    readAt: IsoTime,
});
/** One check: which, its result, and — when it fails — why it matters and how to fix it. */
export const ScaCheck = z.object({
    policyId: ScaPolicyId,
    id: z.number().int().nonnegative(),
    title: z.string().min(1).max(SCA_LIMITS.title),
    result: ScaCheckResult,
    /** Why it could not be judged; null unless not applicable. */
    reason: z.string().max(SCA_LIMITS.reason).nullable(),
    rationale: z.string().max(SCA_LIMITS.rationale).nullable(),
    remediation: z.string().max(SCA_LIMITS.remediation).nullable(),
    /** What it looked at: `findmnt --kernel /tmp`, `/etc/ssh/sshd_config`. */
    subject: z.string().max(SCA_LIMITS.subject).nullable(),
    compliance: ScaCompliance,
    /** Since when it has failed, as far as the API has seen; null unless failing. */
    failingSince: IsoTime.nullable(),
    /** What a person recorded against it on this server (P11.20); null if nothing. */
    exception: CheckException.nullable().default(null),
});
export const RootcheckStatus = z.enum(['outstanding', 'solved']);
export const RootcheckFinding = z.object({
    /** Rootcheck's own words: "File '/dev/.x' present on /dev. Possible hidden file." */
    title: z.string().min(1).max(1000),
    status: RootcheckStatus,
    firstSeenAt: IsoTime,
    lastSeenAt: IsoTime,
});
/** `GET /v1/servers/:id/hardening`: what the manager found on the server. */
export const ServerHardening = z.object({
    policies: z.array(ScaPolicySummary).max(20),
    /** Failed first, then not applicable, then passed; by policy and id within each. */
    checks: z.array(ScaCheck).max(2000),
    rootcheck: z.object({
        /** The last scan's end; null before the API has read one. */
        scannedAt: IsoTime.nullable(),
        findings: z.array(RootcheckFinding).max(500),
    }),
    /** When the API last read this server's findings; null where it never has. */
    readAt: IsoTime.nullable(),
});
/** `cis: "1.1.2.1.1"`, `pci_dss_v4.0: "1.2.5,2.2.4"`, as the API gives them, as lists. */
export function complianceOf(pairs) {
    const out = {};
    for (const { key, value } of pairs.slice(0, SCA_LIMITS.complianceKeys)) {
        const values = value
            .split(',')
            .map((each) => each.trim())
            .filter((each) => each !== '')
            .slice(0, SCA_LIMITS.complianceValues);
        out[key] = [...(out[key] ?? []), ...values].slice(0, SCA_LIMITS.complianceValues);
    }
    const parsed = ScaCompliance.safeParse(out);
    return parsed.success ? parsed.data : undefined;
}
/**
 * A check's MITRE ATT&CK tags from its compliance, as an event's `raw.rule.mitre`
 * (P11.8.6): technique ids as given, tactic ids (`TA0005`) as the names Wazuh's
 * rules use, so an SCA event is found by the same Events filter. Names of
 * techniques are not in the policy: left empty.
 */
export function scaMitre(compliance) {
    const id = (compliance['mitre_techniques'] ?? []).filter((each) => MitreTechniqueId.safeParse(each).success);
    const tactic = (compliance['mitre_tactics'] ?? [])
        .map((each) => MITRE_TACTIC_IDS[each])
        .filter((each) => each !== undefined);
    if (id.length === 0 && tactic.length === 0)
        return undefined;
    return { id: id.slice(0, 10), tactic: [...new Set(tactic)].slice(0, 10), technique: [] };
}
//# sourceMappingURL=wazuh-findings.js.map